// components/Settings/knowledge/KnowledgeHub.jsx
import { useEffect, useState } from "react";
import KnowledgeWorkspace from "./KnowledgeWorkspace";
import { getAllDBConfigs } from "../../../apiWithDBConfig";
import { Database, FileCode, ChevronDown } from "lucide-react";

const INSTRUCTION_ROUTE_TO_TAB = {
  instructions: "general",
  expressions: "expressions",
  "query-instructions": "queries",
};

export default function KnowledgeHub({ initialSubTab = null }) {
  const [configs, setConfigs] = useState([]);
  const [activeConfigId, setActiveConfigId] = useState(null);
  const [activeMode, setActiveMode] = useState(
    () => (INSTRUCTION_ROUTE_TO_TAB[initialSubTab] ? "instructions" : "data")
  ); // data | instructions
  const [loading, setLoading] = useState(true);

  /* Load databases */
  useEffect(() => {
    setLoading(true);
    getAllDBConfigs()
      .then(({ data }) => {
        const list = data || [];
        setConfigs(list);
        if (list.length) {
          setActiveConfigId(list[0].id);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (INSTRUCTION_ROUTE_TO_TAB[initialSubTab]) {
      setActiveMode("instructions");
    }
  }, [initialSubTab]);

  const activeConfig = configs.find((db) => db.id === activeConfigId) || null;


  return (
    <div className="h-full flex flex-col">

      {/* ================= COMPACT HEADER ================= */}
      <div className="flex-shrink-0 bg-background">
        <div className="px-6 py-2">
          <div className="flex items-center justify-between gap-4">
            
            {/* Left: Compact Database Selector */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="relative min-w-[240px]">
                <select
                  value={activeConfigId || ""}
                  onChange={(e) => setActiveConfigId(Number(e.target.value))}
                  disabled={loading || configs.length === 0}
                  className="w-full appearance-none border border-border rounded-lg px-3 py-2 pr-9 text-sm font-medium bg-surface text-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {configs.length === 0 ? (
                    <option>No databases</option>
                  ) : (
                    configs.map((db) => (
                      <option key={db.id} value={db.id}>
                        {db.database_name}
                      </option>
                    ))
                  )}
                </select>
                <ChevronDown 
                  size={14} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
                />
              </div>

            
            </div>

            {/* Right: Compact Mode Switcher */}
            <div className="flex-shrink-0">
              <div className="inline-flex bg-muted p-1 rounded-lg border border-border">
                <button
                  onClick={() => setActiveMode("data")}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                    ${
                      activeMode === "data"
                        ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                        : "text-muted-foreground hover:text-foreground hover:bg-hover-bg"
                    }
                  `}
                >
                  <Database size={14} />
                  <span>Data</span>
                </button>

                <button
                  onClick={() => setActiveMode("instructions")}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                    ${
                      activeMode === "instructions"
                        ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                        : "text-muted-foreground hover:text-foreground hover:bg-hover-bg"
                    }
                  `}
                >
                  <FileCode size={14} />
                  <span>Instructions</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <KnowledgeWorkspace
          activeMode={activeMode}
          configId={activeConfigId}
          configDbType={activeConfig?.db_type || null}
          initialInstructionTab={INSTRUCTION_ROUTE_TO_TAB[initialSubTab] || "general"}
          loading={loading}
        />
      </div>
    </div>
  );
}
